// src/widgets/ClassCoursesModal.jsx
import { useEffect, useMemo, useState } from "react"
import { X, Library, Trash2, LoaderCircle, Plus, Search, CheckSquare, Square, Filter } from "lucide-react"
import Select from "react-select"
import { AnimatePresence, motion } from "framer-motion"
import ConfirmModal from "@/widgets/ConfirmModal"
import { getClassCourses, unmapClassCourses, mapClassCourses, getCourses } from "@/api/kuizy"
import { formatDateTime } from "@/settings/master"
import AddLearnersModal from "@/widgets/AddLearnersModal"
import { set } from "date-fns"
export default function ClassCoursesModal({ classId, className, onClose }) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [alert, setAlert] = useState({ open: false, msg: "" })
  const [searchIn, setSearchIn] = useState("")
  const [search, setSearch] = useState("")
  const [status, setStatus] = useState("")
  const [delItem, setDelItem] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [picker, setPicker] = useState(false)

  const [addLearner, setAddLearner] = useState({ open: false, courseId: null, courseName: "" })
  const load = () => {
    setLoading(true)
    getClassCourses(classId)
      .then(r => {
        const data = Array.isArray(r.data) ? r.data : r.data?.data ?? []
        setRows(data)
      })
      .catch(() => setAlert({ open: true, msg: "โหลดข้อมูลไม่สำเร็จ" }))
      .finally(() => setLoading(false))
  }
  useEffect(load, [classId])

  const list = useMemo(() => {
    const t = search.trim().toLowerCase()
    return rows.filter(c => {
      const okText =
        !t ||
        (c.course_name || c.name || "").toLowerCase().includes(t) ||
        (c.course_code || c.code || "").toLowerCase().includes(t)
      if (!okText) return false
      if (!status) return true
      const act = c.active === true || c.active === 1
      return status === "active" ? act : !act
    })
  }, [rows, search, status])

  const confirmDelete = async () => {
    if (!delItem) return
    setDeleting(true)
    try {
      const id = delItem.course_id || delItem.id
      await unmapClassCourses(classId, [id])
      setDelItem(null)
      load()
    } catch {
      setAlert({ open: true, msg: "ไม่สามารถลบได้" })
    } finally {
      setDeleting(false)
    }
  }

  const statusOptions = [
    { value: "", label: "ทั้งหมด" },
    { value: "active", label: "ใช้งาน" },
    { value: "inactive", label: "ปิด" },
  ]
  const statusValue = statusOptions.find(o => o.value === status)

  return (
    <AnimatePresence>
      <motion.div
        key="ccBack"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-[1000] flex items-center justify-center p-4"
      >
        <motion.div
          key="ccCard"
          initial={{ y: 32, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 32, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="w-full h-full sm:h-[95vh] sm:w-[95%]   bg-white rounded-lg shadow-lg flex flex-col overflow-hidden"
        >
          <header className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-info">
            <h2 className="flex items-center gap-2 text-sm sm:text-base font-semibold">
              <Library size={18} /> หลักสูตรของห้องเรียน “{className}” ({list.length})
            </h2>
            <h3 className="text-sm text-gray-600">
              {rows.length > 0 && (
                <>จำนวนผู้เรียนทั้งหมด: {rows[0].class_count} คน</>
              )}
            </h3>
            <button onClick={onClose} className="text-gray-600 hover:text-primary">
              <X size={20} />
            </button>
          </header>

          <div className="px-4 py-2 border-b border-gray-200">
            <button
              onClick={() => setPicker(true)}
              className="inline-flex items-center gap-1 bg-primary hover:bg-secondary text-white text-sm rounded-lg px-3 py-1"
            >
              <Plus size={14} /> เพิ่มหลักสูตร
            </button>
          </div>

          <div className="flex flex-wrap gap-4 items-end px-4 py-2 bg-info border-b border-gray-200">
            <div className="flex flex-col gap-1 w-44">
              <label className="flex items-center gap-1 text-sm">
                <Filter size={16} /> สถานะ
              </label>
              <Select
                className="rs-select"
                classNamePrefix="rs"
                options={statusOptions}
                value={statusValue}
                onChange={o => setStatus(o?.value ?? "")}
                isSearchable={false}
              />
            </div>

            <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
              <label className="flex items-center gap-1 text-sm">
                <Search size={16} /> ค้นหา
              </label>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <div className="flex items-center gap-2">
                  <input
                    value={searchIn}
                    onChange={e => setSearchIn(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && setSearch(searchIn)}
                    placeholder="ชื่อ/รหัสหลักสูตร"
                    className="w-full border border-gray-300 rounded-lg pl-10 px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
                  />
                  <button
                    onClick={() => setSearch(searchIn)}
                    className="inline-flex items-center gap-1 bg-secondary hover:bg-primary text-white rounded-lg px-3 py-2 text-sm"
                  >
                    <Search size={16} /> ค้นหา
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-auto">
            <table className="min-w-[900px] w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-2 px-3 w-12 text-left">#</th>
                  <th className="py-2 px-3 text-left">รหัสหลักสูตร</th>
                  <th className="py-2 px-3 text-left">ชื่อหลักสูตร</th>
                  <th className="py-2 px-3 text-center">ผู้เรียน</th>
                  <th className="py-2 px-3 text-left hidden md:table-cell">เพิ่มเมื่อ</th>
                  <th className="py-2 px-3 w-20 text-right">จัดการ</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="text-center py-8">
                      <LoaderCircle className="animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : list.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-gray-500">– ไม่มีข้อมูล –</td>
                  </tr>
                ) : (
                  list.map((c, i) => {
                    const id = c.course_id || c.id
                    return (
                      <tr key={id} className="odd:bg-white even:bg-gray-50">
                        <td className="py-1.5 px-3">{i + 1}</td>
                        <td className="py-1.5 px-3">{c.course_code || c.code}</td>
                        <td className="py-1.5 px-3">{c.course_name || c.name}</td>
                        <td className="py-1.5 px-3 text-center">
                          <button className="text-center text-primary font-bold hover:underline" onClick={() => setAddLearner({ open: true, courseId: c.course_id, courseName: c.course_name || c.name })} title="ดูผู้เรียน">
                            {c.student_count || '-'}
                          </button>
                        </td>
                        <td className="py-1.5 px-3 hidden md:table-cell">{formatDateTime(c.created_at)}</td>
                        <td className="py-1.5 px-3 text-right">
                          <button onClick={() => setDelItem(c)} className="text-red-600 hover:text-red-800" title="ลบ">
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>

          {addLearner.open && <AddLearnersModal
            courseId={addLearner.courseId}
            courseName={addLearner.courseName}
            classId={classId}
            open={open}
            onClose={() => setAddLearner({ open: false, courseId: null, courseName: "" })}
            onSave={(success) => {
              if (success) {
                load() // 👈 เรียก API ใหม่
              }
            }}
          />}


          <AnimatePresence>
            {picker && (
              <CoursesPickerModal
                classId={classId}
                mappedIds={new Set(rows.map(r => r.course_id || r.id))}
                onSuccess={() => {
                  setPicker(false)
                  load()
                }}
                onClose={() => setPicker(false)}
              />
            )}
          </AnimatePresence>

          {delItem && (
            <ConfirmModal
              type="confirm"
              title="ยืนยันการลบ"
              message={`ลบ “${delItem.course_name || delItem.name}” ออกจากห้องเรียนนี้ ?`}
              pending={deleting}
              onConfirm={confirmDelete}
              onCancel={() => setDelItem(null)}
            />
          )}
          {alert.open && (
            <ConfirmModal
              type="alert"
              title="แจ้งเตือน"
              message={alert.msg}
              onClose={() => setAlert({ open: false, msg: "" })}
            />
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

function CoursesPickerModal({ classId, mappedIds, onSuccess, onClose }) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchIn, setSearchIn] = useState("")
  const [search, setSearch] = useState("")
  const [status, setStatus] = useState("active")
  const [selected, setSelected] = useState({})
  const [saving, setSaving] = useState(false)

  const load = () => {
    setLoading(true)
    getCourses({ search: search || undefined, status })
      .then(r => {
        const data = Array.isArray(r.data) ? r.data : r.data?.data ?? []
        const filtered = data.filter(c => !mappedIds.has(c.id))
        setRows(filtered)
        setSelected({})
      })
      .finally(() => setLoading(false))
  }
  useEffect(load, [status, search])

  const idsOnPage = useMemo(() => rows.map(c => c.id), [rows])
  const allSelected = rows.length > 0 && idsOnPage.every(id => selected[id])
  const selectedIds = useMemo(() => Object.keys(selected).filter(k => selected[k]), [selected])
  const selectedCount = selectedIds.length

  const toggleOne = id => {
    setSelected(s => {
      const n = { ...s }
      if (n[id]) delete n[id]
      else n[id] = true
      return n
    })
  }

  const toggleAll = () => {
    if (allSelected) {
      const n = { ...selected }
      idsOnPage.forEach(id => delete n[id])
      setSelected(n)
    } else {
      const n = { ...selected }
      idsOnPage.forEach(id => (n[id] = true))
      setSelected(n)
    }
  }

  const save = async () => {
    if (selectedIds.length === 0) return
    setSaving(true)
    try {
      await mapClassCourses(classId, selectedIds)
      onSuccess && onSuccess()
    } finally {
      setSaving(false)
    }
  }

  const statusOptions = [
    { value: "", label: "ทั้งหมด" },
    { value: "active", label: "ใช้งาน" },
    { value: "inactive", label: "ปิด" },
  ]
  const statusValue = statusOptions.find(o => o.value === status)

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-[1100] flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ y: 28, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 28, opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="w-full h-full sm:h-[90vh] sm:w-[90%] bg-white rounded-lg shadow-lg flex flex-col overflow-hidden"
      >
        <header className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-info">
          <h3 className="font-semibold text-sm sm:text-base">เลือกหลักสูตร</h3>
          <button onClick={onClose} className="text-gray-600 hover:text-primary">
            <X size={20} />
          </button>
        </header>

        <div className="flex flex-wrap gap-4 items-end px-4 py-2 bg-info border-b border-gray-200">
          <div className="relative flex-1 min-w-[220px]">
            <label className="flex items-center gap-1 text-sm mb-1">
              <Search size={16} /> ค้นหา
            </label>
            <Search size={16} className="absolute left-3 top-[42px] -translate-y-1/2 text-gray-500 pointer-events-none" />
            <div className="flex items-center gap-2">
              <input
                value={searchIn}
                onChange={e => setSearchIn(e.target.value)}
                onKeyDown={e => e.key === "Enter" && setSearch(searchIn)}
                placeholder="ค้นหาชื่อ/รหัสหลักสูตร"
                className="w-full border border-gray-300 rounded-lg pl-10 px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
              />
              <button
                onClick={() => setSearch(searchIn)}
                className="inline-flex items-center gap-1 bg-secondary hover:bg-primary text-white rounded-lg px-3 py-2 text-sm"
              >
                <Search size={16} /> ค้นหา
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-1 w-44">
            <label className="flex items-center gap-1 text-sm">
              <Filter size={16} /> สถานะ
            </label>
            <Select
              className="rs-select"
              classNamePrefix="rs"
              options={statusOptions}
              value={statusValue}
              onChange={o => setStatus(o?.value ?? "")}
              isSearchable={false}
            />
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          <table className="min-w-[900px] w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="py-2 px-3 w-12 text-left">
                  <button
                    onClick={toggleAll}
                    className="text-primary hover:text-primary/80 disabled:opacity-50"
                    disabled={rows.length === 0}
                  >
                    {allSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                  </button>
                </th>
                <th className="py-2 px-3 text-left">รหัสหลักสูตร</th>
                <th className="py-2 px-3 text-left">ชื่อหลักสูตร</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={3} className="text-center py-8">
                    <LoaderCircle className="animate-spin mx-auto" />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={3} className="text-center py-8 text-gray-500">– ไม่มีข้อมูล –</td>
                </tr>
              ) : (
                rows.map(c => {
                  const id = c.id
                  const checked = !!selected[id]
                  return (
                    <tr
                      key={id}
                      onClick={() => toggleOne(id)}
                      className={`cursor-pointer ${checked ? "bg-primary/10" : "odd:bg-white even:bg-gray-50"} whitespace-nowrap`}
                    >
                      <td className="py-2 px-3 text-center">
                        {checked ? <CheckSquare size={16} /> : <Square size={16} />}
                      </td>
                      <td className="py-1.5 px-3">{c.code}</td>
                      <td className="py-1.5 px-3">{c.name}</td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="p-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-600">เลือกแล้ว {selectedCount} รายการ</div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3 py-2 rounded-lg border border-gray-300 text-sm">ยกเลิก</button>
            <button
              onClick={save}
              disabled={selectedCount === 0 || saving}
              className="inline-flex items-center gap-2 bg-primary hover:bg-secondary text-white rounded-lg px-3 py-2 text-sm disabled:opacity-50"
            >
              {saving && <LoaderCircle size={16} className="animate-spin" />}
              <Plus size={16} /> เพิ่มหลักสูตร
            </button>
          </div>
        </div>

      </motion.div>
    </motion.div>
  )
}
